import React, { useEffect, useRef, useState } from 'react';
import styles from './SearchPicker.module.css';

/**
 * Generic typeahead picker.
 *
 * Used by ticket-creation forms to look up customers / SKUs / etc. by
 * typing instead of pasting a numeric ID. Debounces input by 250ms,
 * shows results in a dropdown, and emits both the id and a display label
 * so the form can echo what was selected.
 *
 * The fetch function is injected so the same component works for any
 * resource (customers, SKUs, orders, drivers, ...).
 */

export interface SearchPickerOption {
  id: number;
  label: string;
  sublabel?: string;
}

interface Props {
  placeholder?: string;
  value: SearchPickerOption | null;
  onChange: (option: SearchPickerOption | null) => void;
  /** Returns at most ~10 results for a given query (already mapped to options). */
  fetcher: (query: string) => Promise<SearchPickerOption[]>;
  required?: boolean;
  disabled?: boolean;
  /** Optional minimum chars before searching (default 1). */
  minChars?: number;
}

export const SearchPicker: React.FC<Props> = ({
  placeholder = 'Search…',
  value,
  onChange,
  fetcher,
  required,
  disabled,
  minChars = 1,
}) => {
  const [query, setQuery] = useState(value?.label ?? '');
  const [results, setResults] = useState<SearchPickerOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  // Keep the visible input text in sync when the parent clears the value.
  useEffect(() => {
    if (!value) setQuery('');
    else setQuery(value.label);
  }, [value]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    if (query.trim().length < minChars) {
      setResults([]);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetcher(query.trim());
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, open, fetcher, minChars]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSelect = (opt: SearchPickerOption) => {
    onChange(opt);
    setQuery(opt.label);
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <input
        type="text"
        className={styles.input}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value) onChange(null); // typing invalidates a prior selection
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        required={required}
        disabled={disabled}
      />
      {value && (
        <button type="button" className={styles.clear} onClick={handleClear} aria-label="Clear">
          ×
        </button>
      )}
      {open && (results.length > 0 || loading) && (
        <ul className={styles.dropdown}>
          {loading && <li className={styles.loading}>Searching…</li>}
          {!loading && results.map((r) => (
            <li key={r.id}>
              <button type="button" className={styles.option} onClick={() => handleSelect(r)}>
                <span className={styles.optionLabel}>{r.label}</span>
                {r.sublabel && <span className={styles.optionSub}>{r.sublabel}</span>}
              </button>
            </li>
          ))}
          {!loading && results.length === 0 && query.trim().length >= minChars && (
            <li className={styles.empty}>No matches</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchPicker;
